import type { NavPage, NavSection } from "./api";

/**
 * One level of page nesting: a page may have a parent page in the same
 * section, and a child has no children. The nav sends a section's pages flat,
 * in reading order, each naming its parentPageId; these helpers rebuild the
 * tree. A page whose parent is not in the list (unpublished, restricted,
 * deleted), or whose parent is itself a child, shows at top level.
 */

interface TreeInput {
  id: string;
  ordinal: number;
  parentPageId?: string | null;
}

export interface PageNode<T> {
  page: T;
  children: T[];
}

/** Top-level pages, each with its children, siblings in ordinal order. */
export function buildPageTree<T extends TreeInput>(pages: ReadonlyArray<T>): PageNode<T>[] {
  const parentIds = new Map(pages.map((p) => [p.id, p.parentPageId ?? null]));
  const hasParent = (id: string) => {
    const parent = parentIds.get(id) ?? null;
    return parent !== null && parentIds.has(parent);
  };
  const isTop = (id: string) => !hasParent(id) || hasParent(parentIds.get(id)!);
  const byOrdinal = (a: T, b: T) => a.ordinal - b.ordinal;
  return pages
    .filter((p) => isTop(p.id))
    .sort(byOrdinal)
    .map((page) => ({
      page,
      children: pages.filter((c) => c.parentPageId === page.id && !isTop(c.id)).sort(byOrdinal),
    }));
}

/** The pages in reading order: a parent, its children, then the next top-level page. */
export function readingOrder<T extends TreeInput>(pages: ReadonlyArray<T>): T[] {
  return buildPageTree(pages).flatMap((n) => [n.page, ...n.children]);
}

/** A page's place in the tree: its parent (when it is a child) and its children. */
export function pagePlace<T extends TreeInput>(
  pages: ReadonlyArray<T>,
  pageId: string
): { parent: T | null; children: T[] } | null {
  for (const node of buildPageTree(pages)) {
    if (node.page.id === pageId) return { parent: null, children: node.children };
    const child = node.children.find((c) => c.id === pageId);
    if (child) return { parent: node.page, children: [] };
  }
  return null;
}

/**
 * The full-section order after moving `activeId` to `overId`'s place. Both
 * must be in the same sibling list (top level, or one parent's children); a
 * moved parent takes its children with it. Null when they are not siblings.
 */
export function moveWithinSiblings<T extends TreeInput>(
  pages: ReadonlyArray<T>,
  activeId: string,
  overId: string
): string[] | null {
  const tree = buildPageTree(pages);
  const move = (ids: string[]) => {
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0) return null;
    const next = [...ids];
    next.splice(to, 0, ...next.splice(from, 1));
    return next;
  };
  const flatten = (nodes: PageNode<T>[]) => nodes.flatMap((n) => [n.page, ...n.children]);

  const top = move(tree.map((n) => n.page.id));
  if (top) {
    const byId = new Map(tree.map((n) => [n.page.id, n]));
    return flatten(top.map((id) => byId.get(id)!)).map((p) => p.id);
  }
  for (const node of tree) {
    const children = move(node.children.map((c) => c.id));
    if (!children) continue;
    const byId = new Map(node.children.map((c) => [c.id, c]));
    node.children = children.map((id) => byId.get(id)!);
    return flatten(tree).map((p) => p.id);
  }
  return null;
}

/** A section shows in the reading nav only with a published page the viewer can read. */
export function hasReadablePage(section: Pick<NavSection, "pages">): boolean {
  return section.pages.some((p: NavPage) => p.published);
}
